import {
  Calculate as CalculateIcon,
  FileDownload as FileDownloadIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { calculateRateCost, formatMoney } from 'lib/functions/utils';
import React from 'react';

const CustomTooltip = (
  tableData: any,
  noHeader?: boolean,
  column1?: string,
  column2?: string,
) => (
  <Table size="small">
    {!noHeader && (
      <TableHead>
        <TableRow>
          <TableCell>{column1 || 'Costo'}</TableCell>
          <TableCell>{column2 || 'Valor'}</TableCell>
        </TableRow>
      </TableHead>
    )}
    <TableBody>
      {tableData.map((row: any) => (
        <TableRow key={row.index}>
          <TableCell>{row.name}</TableCell>
          <TableCell>{row.value}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

interface SimulationDetailsPanelProps {
  expanded: boolean;
  onAccordionChange: (
    panel: string,
  ) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  operationDetails: any;
  selectedRequest: any;
  requestConfiguration: any;
  businessBlacklisted: boolean;
  stakeholdersBlacklisted: string[];
  onExportSimulation: () => void;
}

const SimulationDetailsPanel: React.FC<SimulationDetailsPanelProps> = ({
  expanded,
  onAccordionChange,
  operationDetails,
  selectedRequest,
  requestConfiguration,
  businessBlacklisted,
  stakeholdersBlacklisted,
  onExportSimulation,
}) => {
  const operationAmount = operationDetails.operationAmount;
  const operationAdvanced = operationDetails.operationAdvanced;
  const operationCostRate = operationDetails.operationCostRate;
  const operationCostComissionFixed =
    operationDetails.operationCostComissionFixed;
  const operationCostComissionVariable =
    operationDetails.operationCostComissionVariable;
  const operationToDepost = operationDetails.operationToDepost;
  const operationIva = operationDetails.operationIva;
  const expensesIva = operationDetails.expensesIva;
  const operationAmountToApply = operationDetails.operationAmountToApply;
  const expensesCost = operationDetails.expensesCost;
  const advance = operationDetails.advance;
  const rate = operationDetails.rate;
  const guaranteeAmount = operationDetails.guaranteeAmount;
  const fundRate = operationDetails.fundRate || 0;
  const spread =
    operationDetails.spread !== undefined
      ? operationDetails.spread
      : rate > 0
        ? (rate - fundRate).toFixed(2)
        : 0;
  const expensesDetails = operationDetails.expensesDetails || [];
  const expensesDetailsTooltip: { name: string; value: string }[] = [];
  for (const expense of expensesDetails) {
    if (expense.amount > 0) {
      expensesDetailsTooltip.push({
        name: expense.name,
        value: formatMoney(expense.amount),
      });
      if (expense.hasIva) {
        expensesDetailsTooltip.push({
          name: `${expense.name} IVA`,
          value: formatMoney(0.19 * expense.amount),
        });
      }
    }
  }

  // All in rate
  const allInCost =
    operationCostRate +
    expensesCost +
    operationCostComissionFixed +
    operationCostComissionVariable +
    operationIva;
  const allInCostRate = ((allInCost / operationAdvanced) * 100).toFixed(2);

  const documents = selectedRequest.documents;
  let finalMonthlyRate = 0;
  for (const document of documents) {
    const realRate = rate / 100;
    const interestRateAdjusted = (realRate * document.daysToCount) / 30;
    const interestCost = document.amountDocument * interestRateAdjusted;

    const fixedProportion =
      (operationCostComissionFixed * document.amountDocument) /
      operationAdvanced;
    const variableProportion =
      (operationCostComissionVariable * document.amountDocument) /
      operationAdvanced;
    const expensesCostProportion =
      (expensesCost * document.amountDocument) / operationAdvanced;
    const operationIvaProportion =
      (operationIva * document.amountDocument) / operationAdvanced;

    const totalCost =
      interestCost +
      fixedProportion +
      variableProportion +
      expensesCostProportion +
      operationIvaProportion;

    const effectiveRate = totalCost / document.amountDocument;
    const monthlyRate = (effectiveRate * 30) / document.daysToCount;

    const monthlyRatePonderated =
      100 * monthlyRate * (document.amountDocument / operationAdvanced);

    finalMonthlyRate += monthlyRatePonderated;
  }

  const amountIVA = !('expensesIVA' in selectedRequest)
    ? requestConfiguration.expensesHasIVA
      ? expensesIva
      : 0
    : selectedRequest.expensesIVA
      ? expensesIva
      : 0;

  let operationCostFundRate = 0;
  let operationCostSpread = 0;

  for (const document of selectedRequest.documents) {
    if (document.active) {
      const documentAmount = document.amount;
      const amountFinanced = Math.round(documentAmount * (advance / 100));
      const { daysToCount } = document;

      operationCostFundRate += calculateRateCost(
        daysToCount,
        amountFinanced,
        fundRate,
        requestConfiguration.factoringOperationsSystemFlag,
        document.paymentDate,
      );
      operationCostSpread += calculateRateCost(
        daysToCount,
        amountFinanced,
        spread,
        requestConfiguration.factoringOperationsSystemFlag,
        document.paymentDate,
      );
    }
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={onAccordionChange('panel1')}
      sx={{
        boxShadow: 'none',
        '&:before': { display: 'none' },
        '& .MuiAccordionSummary-root': {
          minHeight: 'auto',
          padding: '16px 20px',
          backgroundColor: 'white',
          '&.Mui-expanded': {
            minHeight: 'auto',
          },
        },
        '& .MuiAccordionSummary-content': {
          margin: 0,
          '&.Mui-expanded': {
            margin: 0,
          },
        },
        '& .MuiAccordionDetails-root': {
          padding: '0 20px 20px',
          backgroundColor: 'white',
        },
      }}
    >
      <AccordionSummary
        expandIcon={null}
        sx={{
          '& .MuiAccordionSummary-expandIconWrapper': {
            transform: 'none',
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              backgroundColor: 'primary.main',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <CalculateIcon sx={{ fontSize: '1rem' }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                fontSize: '1rem',
                lineHeight: 1.1,
              }}
            >
              Detalles de Simulación
            </Typography>
            <Chip
              label="Financiamiento"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: '18px', fontSize: '0.65rem' }}
            />
          </Box>
          {operationToDepost > 0 && (
            <Box sx={{ textAlign: 'right' }}>
              <Stack
                direction="row"
                alignItems="start"
                justifyContent="flex-end"
                spacing={0.75}
              >
                <Typography variant="h4" color="primary">
                  {formatMoney(operationToDepost)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  CLP
                </Typography>
              </Stack>
            </Box>
          )}
          <IconButton
            size="small"
            onClick={onExportSimulation}
            sx={{
              color: 'primary.main',
              backgroundColor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
              '&:hover': {
                backgroundColor: 'grey.100',
                borderColor: 'grey.300',
              },
              borderRadius: '100px',
              padding: '4px 12px',
            }}
          >
            <FileDownloadIcon sx={{ fontSize: '1rem', paddingRight: '4px' }} />
            <Typography variant="caption" fontWeight={600}>
              Exportar
            </Typography>
          </IconButton>
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Box
          sx={{
            backgroundColor: 'grey.50',
            p: 1.5,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            mb: 2,
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Monto operación
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {formatMoney(operationAmount)} CLP
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Monto a financiar ({advance || 0}%)
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {formatMoney(operationAdvanced)} CLP
                </Typography>
              </Stack>
            </Grid>
            {guaranteeAmount !== 0 && guaranteeAmount !== undefined && (
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Monto en garantía
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(guaranteeAmount)} CLP
                  </Typography>
                </Stack>
              </Grid>
            )}
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="caption" color="text.secondary">
                  Diferencia precio{' '}
                  {selectedRequest.exchangeDays !== undefined &&
                  selectedRequest.exchangeDays !== 0
                    ? `(${selectedRequest.exchangeDays > 0 ? '+' : ''}${selectedRequest.exchangeDays} días)`
                    : ''}
                  ({rate || 0}%)
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {formatMoney(operationCostRate)} CLP
                </Typography>
              </Stack>
            </Grid>
            {(fundRate > 0 || spread !== undefined) && (
              <Grid item xs={12}>
                <Box sx={{ pl: 3, mt: 0.5 }}>
                  <Grid container spacing={0.5}>
                    {fundRate > 0 && (
                      <Grid item xs={12}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem' }}
                          >
                            Tasa del fondo ({fundRate}%)
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem' }}
                          >
                            {formatMoney(operationCostFundRate)} CLP
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                    {spread !== undefined && (
                      <Grid item xs={12}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem' }}
                          >
                            Spread ({spread}%)
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.65rem' }}
                          >
                            {formatMoney(operationCostSpread)} CLP
                          </Typography>
                        </Stack>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            )}
            {operationAmountToApply !== 0 && (
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Documentos aplicados
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(operationAmountToApply)} CLP
                  </Typography>
                </Stack>
              </Grid>
            )}
            {expensesCost !== 0 && (
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Otros gastos{amountIVA > 0 ? ' + IVA' : ''}
                    </Typography>
                    <LightTooltip
                      title={CustomTooltip(
                        expensesDetailsTooltip,
                        false,
                        'Gasto',
                        'Valor',
                      )}
                    >
                      <InfoIcon
                        sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                      />
                    </LightTooltip>
                  </Stack>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(expensesCost + amountIVA)} CLP
                  </Typography>
                </Stack>
              </Grid>
            )}
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Comisión operación + IVA
                  </Typography>
                  <LightTooltip
                    title={CustomTooltip([
                      {
                        name: 'Costo fijo',
                        value: formatMoney(operationCostComissionFixed),
                      },
                      {
                        name: 'Costo variable',
                        value: formatMoney(operationCostComissionVariable),
                      },
                      { name: 'IVA', value: formatMoney(operationIva) },
                      {
                        name: 'Total',
                        value: formatMoney(
                          operationCostComissionFixed +
                            operationCostComissionVariable +
                            operationIva,
                        ),
                      },
                    ])}
                  >
                    <InfoIcon
                      sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                    />
                  </LightTooltip>
                </Stack>
                <Typography variant="caption" fontWeight={600}>
                  {formatMoney(
                    operationCostComissionFixed +
                      operationCostComissionVariable +
                      operationIva,
                  )}{' '}
                  CLP
                </Typography>
              </Stack>
            </Grid>
            <Divider sx={{ my: 1 }} />
            <Grid item xs={12}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Monto a girar
                  </Typography>
                  <LightTooltip
                    title={CustomTooltip(
                      [
                        {
                          name: 'Costo total',
                          value: formatMoney(allInCost),
                        },
                        {
                          name: 'Tasa total (%)',
                          value: `${allInCostRate}%`,
                        },
                        {
                          name: 'Tasa mensual (%)',
                          value: `${finalMonthlyRate.toFixed(2)}%`,
                        },
                      ],
                      true,
                    )}
                  >
                    <InfoIcon
                      sx={{ fontSize: '0.8rem', color: 'text.secondary' }}
                    />
                  </LightTooltip>
                </Stack>
                <Typography variant="body2" fontWeight={700} color="primary">
                  {formatMoney(operationToDepost)} CLP
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {businessBlacklisted && (
          <Alert
            severity="error"
            icon={<WarningIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{
              mt: 2,
              borderRadius: 0.5,
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              border: '1px solid rgba(211, 47, 47, 0.2)',
              py: 0.75,
              '& .MuiAlert-message': { py: 0.25 },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              Cliente en Lista Negra
            </AlertTitle>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              Este cliente se encuentra en la lista negra. Se recomienda revisar
              antes de proceder.
            </Typography>
          </Alert>
        )}

        {stakeholdersBlacklisted.length > 0 && (
          <Alert
            severity="error"
            icon={<WarningIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{
              mt: 2,
              borderRadius: 0.5,
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              border: '1px solid rgba(211, 47, 47, 0.2)',
              py: 0.75,
              '& .MuiAlert-message': { py: 0.25 },
            }}
          >
            <AlertTitle sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              Deudor(es) en Lista Negra.
            </AlertTitle>
            <Typography
              variant="body2"
              sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}
            >
              Los RUT {stakeholdersBlacklisted.join(', ')} se encuentran en la
              lista negra. Se recomienda revisar antes de proceder.
            </Typography>
          </Alert>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default SimulationDetailsPanel;
